import { defaultFontFamily } from 'muiTheme/lightTheme';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import type { AccordionItem } from '../content/useAccordionData';
import { useTranslation } from 'react-i18next';

const AccordionSummeryStyled = styled(AccordionSummary)(() => ({
  padding: '4px 0',
  '&-content.Mui-expanded': {
    margin: '10px 0',
  },
  '& .MuiAccordionSummary-content': {
    margin: '10px 0',
  },
  '&.Mui-expanded': {
    margin: '0 0 4px',
    minHeight: '48px',
  },
}));

export const AccordionStyled = styled(Accordion)(() => ({
  border: 'none',
  boxShadow: 'none',
  width: '100%',
  marginBottom: '0',
  '&.Mui-expanded::after': {
    position: 'absolute',
    left: 0,
    bottom: '0px',
    right: 0,
    height: '1px',
    content: '""',
    opacity: 1,
    backgroundColor: 'rgba(0, 40, 58, 0.15)',
    transition: 'opacity 150mscubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 150mscubic-bezier(0.4, 0, 0.2, 1) 0ms',
  },
  '&.Mui-expanded': {
    margin: 0,
    '&::before': {
      opacity: 1,
    },
  },
}));

const BoxStyled = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  marginLeft: 'auto',
  color: 'rgba(0, 40, 58, 0.60)',
}));

const BoxItemStyled = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}));

export const AccordionDetailsStyled = styled(AccordionDetails)(() => ({
  padding: '0',
}));

const fontSettings = {
  fontFamily: defaultFontFamily,
  fontSize: '14px',
  lineHeight: '20px',
};

const TypographyStyledTitle = styled(Typography)(() => ({
  ...fontSettings,
  color: '#71787E',
}));

const TypographyStyledLabel = styled(Typography)(() => ({
  ...fontSettings,
  color: '#353636',
}));

const PyLabelStyled = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontSize: '12px',
  textTransform: 'uppercase',
  marginBottom: theme.spacing(1.5),
}));

const PySectionStyled = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

type SidePanelAccordionProps = {
  title: string;
  textStyleAccordion: AccordionItem[];
  rowStyleAccordion?: AccordionItem[];
  expanded: string;
  onChange: (value: string) => void;
};

const SidePanelAccordion = ({ title, textStyleAccordion, rowStyleAccordion, expanded, onChange }: SidePanelAccordionProps) => {
  const accordionTitle = title.toLowerCase().replace(/\s/g, '-');
  const { t } = useTranslation();

  return (
    <AccordionStyled expanded={expanded === accordionTitle} onChange={() => onChange(accordionTitle)}>
      <AccordionSummeryStyled expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
        <TypographyStyledTitle data-testid={accordionTitle}>{`${title}`}</TypographyStyledTitle>
      </AccordionSummeryStyled>
      <AccordionDetailsStyled>
        {rowStyleAccordion && <PyLabelStyled>{t('editor.side_panel.accordion.text_style')}</PyLabelStyled>}
        <PySectionStyled>
          {textStyleAccordion?.map((item, indexItem) => (
            <BoxItemStyled key={indexItem}>
              <TypographyStyledLabel data-testid="accordion-item-title">{`${item.componentTitle}`}</TypographyStyledLabel>
              <BoxStyled>{item.components}</BoxStyled>
            </BoxItemStyled>
          ))}
        </PySectionStyled>
        {rowStyleAccordion && (
          <>
            <PyLabelStyled> {t('editor.side_panel.accordion.row_style')} </PyLabelStyled>

            <PySectionStyled>
              {rowStyleAccordion?.map((item, indexItem) => (
                <BoxItemStyled key={indexItem}>
                  <TypographyStyledLabel data-testid="accordion-item-title">{`${item.componentTitle}`}</TypographyStyledLabel>
                  <BoxStyled>{item.components}</BoxStyled>
                </BoxItemStyled>
              ))}
            </PySectionStyled>
          </>
        )}
      </AccordionDetailsStyled>
    </AccordionStyled>
  );
};

export default SidePanelAccordion;
